import React, { useMemo } from 'react';

import { IDelivery, IServerOrder } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { geographicalFeeLabelIdMap } from 'utils/order';
import { getDeliveryFees } from 'utils/order';

import { Divider, Total, TotalLabel, TotalsRow } from './modal-order-details.styled';

const DeliveryPriceDetails = ({ delivery }: { delivery: IDelivery }) => {
  const {
    feeCents: deliveryFeeCents,
    isFreeDelivery,
    deliveryFeeDiscountCents,
    geographicalFeeCents,
    serviceFeeCents,
    smallCartFeeCents,
  } = getDeliveryFees({
    delivery,
  });

  const dropoffState = delivery?.dropoff?.state;

  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId =
    dropoffState && geographicalFeeLabelIdMap.hasOwnProperty(dropoffState)
      ? geographicalFeeLabelIdMap[dropoffState]
      : 'geographicalFee';

  return (
    <>
      {/* deliveryFee */}
      <TotalsRow>
        <TotalLabel>{t('deliveryFee')}</TotalLabel>
        <Total>
          <Currency amount={isFreeDelivery ? deliveryFeeDiscountCents : deliveryFeeCents} />
        </Total>
      </TotalsRow>

      {geographicalFeeCents > 0 && (
        <TotalsRow>
          <TotalLabel>{t(geographicalFeeLabelId as TranslationKey)}</TotalLabel>
          <Total>
            <Currency amount={geographicalFeeCents} />
          </Total>
        </TotalsRow>
      )}

      {serviceFeeCents > 0 && (
        <TotalsRow>
          <TotalLabel>{t('serviceFee')}</TotalLabel>
          <Total>
            <Currency amount={serviceFeeCents} />
          </Total>
        </TotalsRow>
      )}

      {smallCartFeeCents > 0 && (
        <TotalsRow>
          <TotalLabel>{t('smallCartFee')}</TotalLabel>
          <Total>
            <Currency amount={smallCartFeeCents} />
          </Total>
        </TotalsRow>
      )}

      <TotalsRow>
        <TotalLabel>{t('tip')}</TotalLabel>
        <Total>
          <Currency amount={delivery?.tipCents} />
        </Total>
      </TotalsRow>
    </>
  );
};

export const OrderPricesDetails = ({ serverOrder }: { serverOrder: IServerOrder }) => {
  const { cart, delivery } = serverOrder;
  const { subTotalCents, taxCents } = cart;

  const discountsAmount = useMemo(() => {
    return cart?.discounts?.reduce((acc, { value }) => acc + value, 0) || 0;
  }, [cart?.discounts]);

  const donationsAmount = useMemo(() => {
    return cart?.donations?.reduce((acc, { totalCents }) => acc + totalCents, 0) || 0;
  }, [cart?.donations]);

  return (
    <>
      <TotalsRow>
        <TotalLabel>{t('subtotal')}</TotalLabel>
        <Total>
          <Currency amount={subTotalCents} />
        </Total>
      </TotalsRow>
      {discountsAmount > 0 && (
        <TotalsRow>
          <TotalLabel>{t('comboOfferSavings')}</TotalLabel>
          <Total>
            <Currency amount={discountsAmount} />
          </Total>
        </TotalsRow>
      )}
      {donationsAmount > 0 && (
        <TotalsRow>
          <TotalLabel>{t('donation')}</TotalLabel>
          <Total>
            <Currency amount={donationsAmount} />
          </Total>
        </TotalsRow>
      )}
      {delivery && <DeliveryPriceDetails delivery={delivery} />}
      <TotalsRow>
        <TotalLabel>{t('taxes')}</TotalLabel>
        <Total>
          <Currency amount={taxCents} />
        </Total>
      </TotalsRow>

      <Divider />
    </>
  );
};
